import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { DOC_FILE_STATUS_LABELS, labelForStatus } from "../applicants/applicantLabels";
import { ApplicantIdentityWarning } from "../applicants/IdentityNotice";

// Los archivos de UN documento, tal como los maneja el aspirante: lo que ya está
// guardado y lo que está por subir.
//
// Antes esto se repetía en tres sitios y en los tres el botón decía «Reemplazar
// archivo» sin decir cuál: el intake agregaba un archivo más y el rechazado se
// quedaba ahí, dejando el documento en RECHAZADO para siempre. Aquí cada archivo
// guardado lleva SU propio botón de reemplazo, que viaja como `replaceFileId`, y
// agregar es una acción aparte que sólo aparece mientras quede cupo.
//
// `storedFiles` son los archivos ya guardados; `pending` son los escogidos y sin
// guardar, como `({ file, replaceFileId } | undefined)[]` indexado por slot.
// `onPick(slot, file, replaceFileId)` recibe null en `file` para descartar.

const STATUS_COLOR = {
  UPLOADED: "info",
  VALIDATED: "success",
  REJECTED: "error",
};

// 0 = ilimitado.
const capOf = (maxFiles) => {
  const n = Math.floor(Number(maxFiles));
  if (n === 0) return Infinity;
  return Number.isFinite(n) && n > 0 ? n : 1;
};

const fileLabel = (file, index, total) =>
  file?.name || (total > 1 ? `Archivo ${index + 1}` : "documento");

export default function DocumentSlots({
  docDef,
  storedFiles = [],
  pending = [],
  onPick,
  onView,
  disabled = false,
}) {
  // Reemplazar algo ya validado reabre una decisión cerrada, así que se pregunta
  // antes de aceptarlo. Se guarda la selección hasta que el aspirante confirme.
  const [confirm, setConfirm] = useState(null); // { slot, file, target }

  const accept = (docDef?.acceptedMime?.length
    ? docDef.acceptedMime
    : ["application/pdf", "image/*"]).join(",");
  const max = capOf(docDef?.maxFiles);
  const stored = storedFiles.filter((a) => a && a.path);

  // Un slot por archivo guardado (su reemplazo) más los de agregar, que arrancan
  // después para que los índices nunca se pisen.
  const addBase = stored.length;
  const addPending = pending.slice(addBase).filter(Boolean).length;
  // Un slot por hueco que ADMITE el documento, esté ocupado o no. Antes se
  // restaban también los ya escogidos, así que en un documento de un solo archivo
  // el slot desaparecía en cuanto se escogía el archivo — y con él el chip que lo
  // nombraba, dejando la fila vacía y el archivo con toda la pinta de no haberse
  // adjuntado. Se nota sobre todo en el registro público, donde `storedFiles`
  // siempre viene vacío y no hay ninguna otra cosa que mirar en la fila.
  const addSlots = max === Infinity ? addPending + 1 : Math.max(0, max - stored.length);

  const pick = (slot, file, target) => {
    if (!file) { onPick?.(slot, null, null); return; }
    if (target && target.status === "VALIDATED") { setConfirm({ slot, file, target }); return; }
    onPick?.(slot, file, target?.id || null);
  };

  const acceptConfirm = () => {
    if (confirm) onPick?.(confirm.slot, confirm.file, confirm.target.id);
    setConfirm(null);
  };

  return (
    <>
      <Stack spacing={1} sx={{ mt: 0.5 }}>
        {stored.map((a, i) => {
          const staged = pending[i];
          // El nombre del archivo va en su propia línea, pero los botones tienen
          // que nombrarlo igual: con varios archivos, «Ver» y «Reemplazar» a secas
          // no dicen sobre cuál actúan.
          const nombre = fileLabel(a, i, stored.length);
          return (
            <Box key={a.id || i}>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  color={STATUS_COLOR[a.status] || "default"}
                  label={labelForStatus(DOC_FILE_STATUS_LABELS, a.status)}
                />
                <Typography
                  variant="caption"
                  sx={{ minWidth: 0, flexGrow: 1, wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  {nombre}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<VisibilityIcon />}
                  onClick={() => onView?.(a)}
                  aria-label={`Ver ${nombre}`}
                  sx={{ textTransform: "none" }}
                >
                  Ver
                </Button>
                <Button
                  component="label"
                  size="small"
                  variant="outlined"
                  disabled={disabled}
                  startIcon={<SwapHorizIcon />}
                  aria-label={`Reemplazar ${nombre}`}
                  sx={{ textTransform: "none" }}
                >
                  Reemplazar
                  <input
                    hidden
                    type="file"
                    accept={accept}
                    aria-label={`Archivo que reemplaza a ${nombre}`}
                    // Sin esto, escoger el mismo archivo dos veces seguidas no
                    // dispara onChange y el reemplazo parece no hacer nada.
                    onClick={(e) => { e.target.value = ""; }}
                    onChange={(e) => pick(i, e.target.files?.[0] || null, a)}
                  />
                </Button>
              </Stack>
              <ApplicantIdentityWarning identity={a.identity} />
              {staged?.file && (
                <Chip
                  sx={{ mt: 0.5 }}
                  size="small"
                  color="info"
                  variant="outlined"
                  label={`Reemplazar por: ${staged.file.name}`}
                  onDelete={() => pick(i, null)}
                />
              )}
            </Box>
          );
        })}

        {Array.from({ length: addSlots }).map((_, n) => {
          const slot = addBase + n;
          const staged = pending[slot];
          return (
            <Box key={`add-${slot}`}>
              <Button
                component="label"
                variant="outlined"
                size="small"
                disabled={disabled}
                startIcon={<AttachFileIcon />}
                fullWidth
                sx={{ justifyContent: "flex-start", textTransform: "none" }}
              >
                {staged?.file
                  ? "Cambiar archivo"
                  : stored.length
                    ? "Adjuntar otro archivo"
                    : "Adjuntar archivo"}
                <input
                  hidden
                  type="file"
                  accept={accept}
                  onClick={(e) => { e.target.value = ""; }}
                  onChange={(e) => pick(slot, e.target.files?.[0] || null, null)}
                />
              </Button>
              {staged?.file && (
                <Chip
                  sx={{ mt: 0.5 }}
                  size="small"
                  label={staged.file.name}
                  onDelete={() => pick(slot, null)}
                />
              )}
            </Box>
          );
        })}

        {stored.length > 0 && addSlots === 0 && (
          <Typography variant="caption" color="text.secondary">
            Alcanzaste el máximo de archivos de este documento. Para cambiar uno, usa
            «Reemplazar».
          </Typography>
        )}
      </Stack>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Este documento ya fue validado</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" gutterBottom>
              «{fileLabel(confirm?.target, 0, 1)}» ya fue revisado y validado.
            </Typography>
            <Typography variant="body2">
              Si lo reemplazas por «{confirm?.file?.name}», la validación se pierde y el
              documento vuelve a quedar pendiente de revisión.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancelar</Button>
          <Button variant="contained" onClick={acceptConfirm}>Reemplazar de todos modos</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
